import React, { useEffect, useState } from "react";
import { Page } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useLocation } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  Tabs,
  TextInput,
  Textarea,
  LoadingOverlay,
  Text,
  SimpleGrid,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import FormSection from "../../components/formSection";
import PageAndActionAccessList from "../../components/pageAndActionAccessList";
import FunctionList from "../../components/functionList";
import DashboardAccessEditor from "../../components/dashboardAccessEditor";
import { getAllActionKeys, ACTION_ACCESS_ACCESSIBLE } from "../../data/actionRegistry";
import { useTabActionsOptional, notifyListRefresh } from "../../context/tabContext";
import { buildTabKey, normalizePathname } from "../../utils/tabNavigation";
import {
  buildRoleEditSearch,
  buildRoleFormValuesFromData,
} from "../../helpers/userRoleDuplicate";

const apiEntity = "userRole";
const ROLE_EDIT_PATH = "/operation/userRole/edit";
const DEFAULT_RETURN_PATH = "/operation/user";

const DEFAULT_ROLE_LINKS = [{ link: "MCA2", accessRight: 15 }];

const buildDefaultActionLinks = () =>
  getAllActionKeys().map(({ entity, action }) => ({
    entity,
    action,
    accessRight: ACTION_ACCESS_ACCESSIBLE,
  }));

const UserRoleEdit = () => {
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const tabActions = useTabActionsOptional();
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    cname: "",
    description: "",
  });

  const query = new URLSearchParams(location.search);
  const id = query.get("id");
  const mode = query.get("mode");
  const copyFrom = query.get("copyFrom");
  const returnTo = query.get("returnTo") || DEFAULT_RETURN_PATH;
  const isAdd = mode === "add" && !id;

  const form = useForm({
    initialValues: {
      _id: null,
      name: "",
      cname: "",
      description: "",
      links: DEFAULT_ROLE_LINKS,
      dashboards: [],
      actionLinks: buildDefaultActionLinks(),
      accessRight: [],
    },
  });

  const navigateToReturnPath = () => {
    const label =
      normalizePathname(returnTo) === "/operation/userRole"
        ? t("User Role")
        : t("User");
    tabNav.open({ pathname: returnTo, label });
  };

  const handleClose = () => {
    if (tabNav.enabled && tabActions) {
      const listKey = buildTabKey(normalizePathname(returnTo), "");
      const activeTabId = tabActions.getActiveTabId();
      const listTab = tabActions
        .getTabs()
        .find((tab) => tab.key === listKey && tab.id !== activeTabId);
      if (listTab) {
        tabNav.closeActive({ survivorTabId: listTab.id });
        return;
      }
    }
    navigateToReturnPath();
  };

  const loadRole = async (roleId, { duplicate = false } = {}) => {
    setLoading(true);
    try {
      const data = await api.getById({ apiEntity, id: roleId });
      if (!data) {
        throw new Error(t("Role not found"));
      }
      const values = buildRoleFormValuesFromData(data, {
        duplicate,
        defaultActionLinks: buildDefaultActionLinks(),
      });
      form.setValues(values);
      setBasicInfo({
        name: values.name,
        cname: values.cname,
        description: values.description,
      });
    } catch (error) {
      showNotification({
        title: t("Load Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadRole(id);
      return;
    }
    if (isAdd && copyFrom) {
      loadRole(copyFrom, { duplicate: true });
    }
  }, [id, isAdd, copyFrom]);

  const handleSave = async () => {
    const { name } = basicInfo;
    if (!name?.trim()) {
      showNotification({
        title: t("Validation"),
        message: t("English name is required."),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
      return;
    }

    setSaving(true);
    try {
      const values = { ...form.values, ...basicInfo };
      delete values._id;

      let result;
      if (isAdd) {
        result = await api.add({ apiEntity, values });
      } else {
        result = await api.updateById({
          apiEntity,
          id: form.values._id,
          values,
        });
      }

      if (!result?.success) {
        throw new Error(api.getErrorMsg(result?.error));
      }

      showNotification({
        title: t("Save Success"),
        message: t(
          "Role saved. Users must log in again for page, action, and dashboard view changes to apply."
        ),
        color: "green",
        icon: <IconCircleCheck size={18} />,
      });

      notifyListRefresh(returnTo);

      if (isAdd && result.data?._id) {
        tabNav.replace({
          pathname: ROLE_EDIT_PATH,
          search: buildRoleEditSearch(
            { id: result.data._id, mode: "edit" },
            returnTo
          ),
          label: `${t("User Role")}: ${basicInfo.name}`,
        });
      } else if (tabActions?.enabled && basicInfo.name?.trim()) {
        tabActions.updateTabLabelByKey(
          buildTabKey(location.pathname, location.search),
          `${t("User Role")}: ${basicInfo.name.trim()}`
        );
      }
    } catch (error) {
      showNotification({
        title: t("Save Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  const title = isAdd
    ? t("New User Role")
    : `${t("User Role")}: ${basicInfo.name || "..."}`;

  return (
    <Page>
      <LoadingOverlay visible={loading} />
      <PageListHeader
        title={title}
        actionButtons={[
          {
            label: t("Save Role"),
            onClick: handleSave,
            loading: saving,
          },
          {
            label: t("Close"),
            onClick: handleClose,
            variant: "default",
          },
        ]}
      />

      <FormSection title="Basic Info">
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          <TextInput
            label={t("English Name")}
            required
            value={basicInfo.name}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setBasicInfo((prev) => ({ ...prev, name: value }));
            }}
          />
          <TextInput
            label={t("Chinese Name")}
            value={basicInfo.cname}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setBasicInfo((prev) => ({ ...prev, cname: value }));
            }}
          />
        </SimpleGrid>
        <Textarea
          label={t("Description")}
          minRows={3}
          mt="md"
          value={basicInfo.description}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setBasicInfo((prev) => ({ ...prev, description: value }));
          }}
        />
      </FormSection>

      <FormSection title="Access Control">
        <Tabs defaultValue="Pages & Actions" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="Pages & Actions">{t("Pages & Actions")}</Tabs.Tab>
            <Tabs.Tab value="API Control">{t("API Control")}</Tabs.Tab>
            <Tabs.Tab value="Dashboard Views">{t("Dashboard Views")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="Pages & Actions" pt="xl">
            <PageAndActionAccessList
              links={form.values.links}
              actionLinks={form.values.actionLinks}
              setFieldValue={form.setFieldValue}
            />
          </Tabs.Panel>

          <Tabs.Panel value="API Control" pt="xl">
            <Text size="sm" color="dimmed" mb="md">
              {t("API route permissions (switches not yet wired to save).")}
            </Text>
            <FunctionList />
          </Tabs.Panel>

          <Tabs.Panel value="Dashboard Views" pt="xl">
            <DashboardAccessEditor
              dashboards={form.values.dashboards}
              roleName={basicInfo.name}
              setFieldValue={form.setFieldValue}
            />
          </Tabs.Panel>
        </Tabs>
      </FormSection>
    </Page>
  );
};

export default UserRoleEdit;
